import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, X, Users, ChevronRight } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const ReferralMilestoneCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed this session
    const dismissedKey = `referral_card_dismissed_${user?.id}`;
    const isDismissed = sessionStorage?.getItem(dismissedKey);
    if (isDismissed) {
      setDismissed(true);
      setLoading(false);
      return;
    }
    loadReferralCount();
  }, [user]);

  const loadReferralCount = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await supabase
        ?.from('referrals')
        ?.select('id')
        ?.eq('referrer_id', user?.id)
        ?.eq('status', 'converted');
      setReferralCount(data?.length || 0);
    } catch (e) {
      setReferralCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    const dismissedKey = `referral_card_dismissed_${user?.id}`;
    sessionStorage?.setItem(dismissedKey, 'true');
    setDismissed(true);
  };

  // Hide if dismissed or loading or user has reached 10 referrals
  if (loading || dismissed || referralCount >= 10) return null;

  const doctorSummaryProgress = Math.min(referralCount, 5);
  const premiumProgress = Math.min(referralCount, 10);

  const getNextUnlockLabel = () => {
    if (referralCount < 5) {
      const remaining = 5 - referralCount;
      return `Next: Doctor Summary — refer ${remaining} more friend${remaining !== 1 ? 's' : ''}`;
    }
    const remaining = 10 - referralCount;
    return `Next: Full Premium — refer ${remaining} more friend${remaining !== 1 ? 's' : ''}`;
  };

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 mb-4 relative">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pr-6">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Gift className="w-4 h-4 text-green-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-sm">Unlock Premium Free</h3>
          <p className="text-xs text-green-700">{getNextUnlockLabel()}</p>
        </div>
      </div>

      {/* Progress bars */}
      <div className="space-y-3 mb-4">
        {/* Doctor Summary */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium text-gray-700">Doctor Summary</span>
            <span className="text-xs text-gray-500">{doctorSummaryProgress}/5 referrals</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(doctorSummaryProgress / 5) * 100}%` }}
            />
          </div>
          {referralCount >= 5 && (
            <p className="text-xs text-green-600 font-medium mt-0.5">✓ Unlocked!</p>
          )}
        </div>

        {/* Full Premium */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium text-gray-700">Full Premium</span>
            <span className="text-xs text-gray-500">{premiumProgress}/10 referrals</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(premiumProgress / 10) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Invite button */}
      <button
        onClick={() => navigate('/referral-program')}
        className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
      >
        <Users className="w-4 h-4" />
        Invite Friends
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ReferralMilestoneCard;
